<script lang="ts">
	import Markdown from '@astlai/common/Markdown.svelte';
	import type { DocumentDetail, QuotedWebSource } from 'src/types/llm';
	import { transformSourceText, type InlineQuote } from 'src/util/citation-transformer';
	import { onMount } from 'svelte';
	import CloseIcon from 'virtual:icons/material-symbols/close-rounded';
	import FormatQuoteIcon from 'virtual:icons/material-symbols/format-quote-rounded';
	import LinkIcon from 'virtual:icons/material-symbols/link';
	import DocumentTitle from './DocumentTitle.svelte';
	import { t } from './i18n/tranlsator.svelte';
	import SourceBadge, { type Badge } from './SourceBadge.svelte';

	interface Props {
		document?: DocumentDetail;
		source?: QuotedWebSource;
		showBackButton: boolean;
		back: () => void;
		badge?: Badge;
		quotes?: InlineQuote[];
		selectedRefId?: string | null;
	}
	let {
		document,
		source,
		showBackButton,
		back = () => {},
		badge,
		quotes,
		selectedRefId
	}: Props = $props();

	let containerEl: HTMLDivElement = $state(null);

	const transformedText = $derived(
		quotes?.length && document ? transformSourceText(document.text, quotes) : document?.text
	);

	const hostname = $derived.by(() => {
		if (!source) return null;
		try {
			return new URL(source.url).hostname;
		} catch {
			return source.url;
		}
	});

	const sourceUrlWithFragments = $derived.by(() => {
		if (!source) return '';
		const quotes = source.inlineQuotes.filter((q) => q.quotedText);
		if (!quotes.length) return source.url;
		const fragments = quotes.map((q) => {
			const words = q.quotedText.trim().split(/\s+/);
			if (words.length > 4) {
				const start = words.slice(0, 2).join(' ');
				const end = words.slice(-2).join(' ');
				return `${encodeURIComponent(start)},${encodeURIComponent(end)}`;
			}
			return encodeURIComponent(q.quotedText.trim());
		});
		return `${source.url}#:~:text=${fragments.join('&text=')}`;
	});

	function scrollRefIntoView(id?: string | null) {
		if (!id || !containerEl) return;
		const refEl = containerEl.querySelector(`[data-ref-id="${id}"]`);
		refEl?.scrollIntoView({ behavior: 'smooth', block: 'center' });
	}

	$effect(() => {
		if (selectedRefId) scrollRefIntoView(selectedRefId);
	});

	onMount(() => {
		setTimeout(() => scrollRefIntoView(selectedRefId), 250);
	});
</script>

<div class="document-detail">
	<div class="source-header">
		{#if source}
			<SourceBadge
				badge={{ color: source.citation_color, index: source.citation_index }}
				webSource
			/>
			<div class="title-area">
				<span class="title">{source.title}</span>
				<span class="hostname">{hostname}</span>
			</div>
		{:else}
			{#if badge}
				<SourceBadge {badge} />
			{/if}
			<DocumentTitle doc={document} />
		{/if}

		{#if showBackButton}
			<button class="icon-button" onclick={() => back()}><CloseIcon /></button>
		{/if}
	</div>

	<div bind:this={containerEl} class="content">
		{#if source}
			<a
				href={sourceUrlWithFragments}
				target="_blank"
				rel="noopener"
				class="button glass source-link"
			>
				<div class="icon"><LinkIcon /></div>
				<div class="text">
					<span class="label">{t('sources.openWebSource')}</span>
					<span class="url">{source.url}</span>
				</div>
			</a>

			{#if source.description}
				<div class="section">
					<h3>{t('sources.description')}</h3>
					<p>{source.description}</p>
				</div>
			{/if}

			{#if source.inlineQuotes.some((q) => q.quotedText)}
				<div class="section">
					<h3>{t('sources.citedPassages')}</h3>
					<div class="quotes">
						{#each source.inlineQuotes.filter((q) => q.quotedText) as quote}
							<div class="quote-item">
								{#if quote.sub_index}
									<SourceBadge
										badge={{
											color: source.citation_color,
											index: source.citation_index,
											subIndex: quote.sub_index
										}}
										webSource
									/>
								{/if}
								<span class="quoted-text">
									<FormatQuoteIcon class="quote-icon quote-icon-open" />
									...{quote.quotedText}...
									<FormatQuoteIcon class="quote-icon quote-icon-close" />
								</span>
							</div>
						{/each}
					</div>
					<p class="copyright-notice">{t('sources.copyrightNotice')}</p>
				</div>
			{/if}
		{:else if document}
			{#if document.res_href}
				<div>
					<a href={document.res_href} target="_blank" class="button glass source-link">
						<div class="icon"><LinkIcon /></div>
						<div class="text">
							<span class="label">{t('sources.documentLink')}</span>
							<span class="url">{document.res_href}</span>
						</div>
					</a>
				</div>
			{/if}

			<div class="section">
				<h3>{t('sources.summary')}</h3>
				<div class="summary">
					<Markdown source={document.summary} />
				</div>
			</div>

			<div class="section">
				<h3>{t('sources.originalText')}</h3>
				<div class="original">
					<Markdown source={transformedText} />
				</div>
			</div>
		{/if}
	</div>
</div>

<style lang="scss">
	@use '@astlai/common/styles/mixins' as lib;

	.document-detail {
		@include lib.flex-column();
		overflow: hidden;
		margin: 1rem;
		margin-top: 0;
		border-radius: 1rem;
		border: 1px solid var(--border-subtle);
		background: var(--surface-2);

		h3 {
			margin: 0;
			font-size: 1rem;
			line-height: 1rem;
		}

		.source-header {
			@include lib.flex-row();
			@include lib.flex-align();
			gap: 0.5rem;
			flex: 0 0 auto;
			padding: 1rem;
			position: sticky;
			top: 0px;
			border-bottom: 1px solid var(--border-subtle);

			.title-area {
				flex: 1 1 auto;
				@include lib.flex-column();
				gap: 0.125rem;
				overflow: hidden;

				.title {
					@include lib.ellipsis();
					font-weight: 500;
				}

				.hostname {
					@include lib.ellipsis();
					font-size: 0.75rem;
					color: var(--text-secondary);
				}
			}

			.icon-button {
				flex: 0 0 auto;
				align-self: flex-start;
			}
		}

		.content {
			flex: 1 1 auto;
			overflow: auto;
			overflow: overlay;
			@include lib.flex-column();
			gap: 1rem;
			font-size: 0.875rem;
			line-height: 1.5rem;
			padding: 1rem;

			.source-link {
				@include lib.flex-row();
				@include lib.flex-align();
				gap: 0.25rem;
				height: auto;
				padding-left: 0.5rem;

				.icon {
					padding: 0 0.25rem;
					flex: 0 0 auto;
					display: flex;
				}

				.text {
					@include lib.flex-column();
					@include lib.flex-align(flex-start, stretch);
					gap: 0.25rem;
					overflow: hidden;

					span {
						@include lib.ellipsis();
						text-align: left;

						&.label {
							font-weight: 500;
							font-size: 1rem;
							line-height: 1rem;
						}

						&.url {
							font-size: 0.75rem;
							line-height: 0.75rem;
						}
					}
				}
			}

			.section {
				@include lib.flex-column();

				h3 {
					margin-bottom: 1rem;
				}

				p {
					margin: 0;
				}

				.copyright-notice {
					margin-top: 0.75rem;
					font-size: 0.75rem;
					color: var(--text-secondary);
					font-style: italic;
				}
			}

			.quotes {
				@include lib.flex-column();
				gap: 0.75rem;

				.quote-item {
					.quoted-text {
						font-style: italic;

						:global(.quote-icon) {
							font-size: 0.875rem;
							vertical-align: middle;
							opacity: 0.5;
						}

						:global(.quote-icon-close) {
							transform: rotate(180deg);
							display: inline-block;
							vertical-align: top;
						}
					}

					.highlight-link {
						font-size: 0.75rem;
						padding-left: 0.75rem;
					}
				}
			}
		}
	}
</style>
